import type { AppState, User } from "@auth0/auth0-react";
import { Auth0Provider } from "@auth0/auth0-react";
import { clearSilentReauth } from "../silentReauth";
import { getPersistedOrgId, setPersistedOrgId } from "../tenant/persistedOrg";

const auth0Domain = import.meta.env.VITE_AUTH0_DOMAIN;
const clientId = import.meta.env.VITE_AUTH0_CLIENT_ID;
const audience = import.meta.env.VITE_AUTH0_AUDIENCE;
const AUTH_DISABLED = import.meta.env.VITE_AUTH_DISABLED === "true";

interface AuthenticationProviderProps {
  children: React.ReactNode;
}

function onRedirectCallback(appState?: AppState, user?: User) {
  clearSilentReauth(); // login succeeded — reset the silent-renewal guard
  // Capture the org just logged into (from the org-scoped id-token) so the
  // post-login reload scopes its silent token calls to the same organization.
  // auth0-spa-js keys its token cache by org; an org-less getAccessTokenSilently()
  // against an org-scoped session misses the cache and, since silent renewal
  // can't run on a localhost callback, rejects with "external interaction
  // required" — bouncing straight back to the org prompt. Persisting here makes
  // AuthProvider re-mount org-scoped so the cache hits instead.
  const orgId: unknown = user?.org_id;
  if (typeof orgId === "string") {
    setPersistedOrgId(orgId);
  }
  const returnTo = appState?.returnTo ?? "/";
  window.location.replace(returnTo);
}

export function AuthProvider({ children }: AuthenticationProviderProps) {
  // Dev-only escape hatch: when `VITE_AUTH_DISABLED=true`, skip
  // mounting `Auth0Provider` entirely. Auth0's SDK throws synchronously
  // on a non-secure origin (e.g. accessing the dev server from another
  // machine via the LAN IP), which would otherwise crash the whole app
  // before `AuthGuard`'s bypass has a chance to run. Pairs with the
  // API-side `APPRETIO_AUTH_DISABLED` flag.
  if (AUTH_DISABLED) {
    return <>{children}</>;
  }

  // The tenant (Auth0 organization) every request is scoped to — the user's
  // persisted selection. The SPA MUST send an organization on *every* call:
  // with organization_usage=require, a request without one can't complete
  // silently (checkSession + token renewal use prompt=none, which can't satisfy
  // the org prompt) and loops. A first-time user has no persisted org yet —
  // AuthGuard's LoginScreen discovers it from their email before redirecting.
  const orgId = getPersistedOrgId();

  return (
    <Auth0Provider
      domain={auth0Domain}
      clientId={clientId}
      cacheLocation="localstorage"
      useRefreshTokens
      onRedirectCallback={onRedirectCallback}
      authorizationParams={{
        redirect_uri: window.location.origin,
        audience: audience,
        ...(orgId ? { organization: orgId } : {})
      }}
    >
      {children}
    </Auth0Provider>
  );
}
